import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Title,
  Text,
  Radio,
  Button,
  Stack,
  Group,
  Paper,
  Badge,
  Center,
  Loader,
  ActionIcon,
  Tooltip,
  Alert,
  Progress,
  Card
} from '@mantine/core';
import {
  IconPlayerPause,
  IconPlayerPlay,
  IconCheck,
  IconUsers,
  IconTrophy,
  IconClock,
  IconAlertTriangle,
  IconBook,
  IconTag
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import examenService from '../services/examenService';
import { MultijugadorService } from '../services/multijugadorService';
import { WebSocketService } from '../services/webSocketService';
import {
  IniciarExamenResponse,
  RespuestaUsuario,
  FinalizarExamenRequest
} from '../types/examen.types';
import { 
  SalaMultijugadorResponseDTO, 
  MultijugadorNavigationState,
  RankingSalaDTO 
} from '../types/multijugador';
import { authService } from '../services/authService';
import QuejaSugerenciaButton from '../components/QuejaSugerenciaButton';

const ExamenMultijugadorPage: React.FC = () => {
  console.log('🏁 [EXAMEN-MULTI] ====== COMPONENTE MONTADO ======');
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUserFromStorage();

  console.log('📊 [EXAMEN-MULTI] Datos de navegación:', {
    locationState: location.state,
    currentUser: currentUser ? {
      id: currentUser.id,
      username: currentUser.username,
      nombre: currentUser.nombre
    } : null
  });

  const navigationState = location.state as MultijugadorNavigationState;
  const sala = navigationState?.sala;
  
  console.log('🏠 [EXAMEN-MULTI] Datos de la sala:', sala ? {
    codigoSala: sala.codigoSala,
    nombreSala: sala.nombreSala,
    estado: sala.estado,
    cantidadParticipantes: sala.cantidadParticipantes,
    participantes: sala.participantes.map(p => ({
      id: p.id,
      usuarioId: p.usuarioId,
      nombreUsuario: p.nombreUsuario,
      intentoExamenId: p.intentoExamenId,
      estado: p.estado
    }))
  } : null);

  const [examenData, setExamenData] = useState<IniciarExamenResponse | null>(null);
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState<Map<number, string | null>>(new Map());
  const [tiempoInicio, setTiempoInicio] = useState<Date | null>(null);
  const [tiempoTranscurrido, setTiempoTranscurrido] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pausado, setPausado] = useState(false);
  const [finalizando, setFinalizando] = useState(false);
  const [intentoId, setIntentoId] = useState<number | null>(null);
  const [primeraLlamada, setPrimeraLlamada] = useState(true);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const webSocketService = WebSocketService.getInstance();
  const [inicializado, setInicializado] = useState(false);

  useEffect(() => {
    console.log('🏁 [EXAMEN-MULTI] useEffect ejecutándose');
    console.log('   - sala:', !!sala);
    console.log('   - currentUser:', !!currentUser);
    console.log('   - inicializado:', inicializado);

    if (!sala || !currentUser) {
      console.warn('⚠️ [EXAMEN-MULTI] Datos faltantes - redirigiendo a /multijugador');
      console.warn('   - sala:', !!sala);
      console.warn('   - currentUser:', !!currentUser);
      navigate('/multijugador');
      return;
    }

    if (inicializado) {
      console.log('⏭️ [EXAMEN-MULTI] Ya fue inicializado, saltando...');
      return;
    }

    console.log('✅ [EXAMEN-MULTI] Datos válidos - inicializando examen...');
    setInicializado(true);
    inicializarExamen();
    return () => {
      console.log('🧹 [EXAMEN-MULTI] Cleanup - limpiando interval');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const inicializarExamen = async () => {
    console.log('🚀 [EXAMEN-MULTI] ====== INICIALIZANDO EXAMEN ======');
    try {
      setLoading(true);
      console.log('🔄 [EXAMEN-MULTI] Configurando loading...');

      console.log('🔍 [EXAMEN-MULTI] Buscando participante en la sala...');
      console.log('   - usuarioId buscado:', currentUser!.id);
      console.log('   - participantes en sala:', sala!.participantes.map(p => ({ id: p.id, usuarioId: p.usuarioId, nombreUsuario: p.nombreUsuario, intentoExamenId: p.intentoExamenId })));
      
      // Obtener el intento de examen del participante
      const participante = sala!.participantes.find(p => p.usuarioId === currentUser!.id);
      console.log('👤 [EXAMEN-MULTI] Participante encontrado:', participante);
      
      if (!participante?.intentoExamenId) {
        console.error('❌ [EXAMEN-MULTI] No se encontró intento de examen para el participante');
        console.error('   - participante:', participante);
        console.error('   - intentoExamenId:', participante?.intentoExamenId);
        throw new Error('No se encontró tu intento de examen');
      }
      
      console.log('✅ [EXAMEN-MULTI] Intento de examen encontrado:', participante.intentoExamenId);

      setIntentoId(participante.intentoExamenId);
      console.log('📝 [EXAMEN-MULTI] IntentoId configurado:', participante.intentoExamenId);

      console.log('📄 [EXAMEN-MULTI] Cargando datos del examen...');
      // Cargar datos del examen
      const progresoResponse = await examenService.recuperarProgreso(participante.intentoExamenId);
      console.log('✅ [EXAMEN-MULTI] Datos del examen cargados:', {
        totalPreguntas: progresoResponse.examenData?.totalPreguntas,
        preguntasLength: progresoResponse.examenData?.preguntas?.length,
        tipoExamen: progresoResponse.examenData?.tipoExamen
      });
      setExamenData(progresoResponse.examenData);
      
      console.log('⏰ [EXAMEN-MULTI] Configurando tiempo de inicio...');
      const inicio = new Date();
      setTiempoInicio(inicio);
      iniciarTemporizador(inicio);

      console.log('🌐 [EXAMEN-MULTI] Conectando WebSocket para ranking...');
      // Conectar WebSocket para ranking en tiempo real (no crítico)
      try {
        await conectarWebSocket();
      } catch (wsError) {
        console.warn('⚠️ [EXAMEN-MULTI] Error en WebSocket (no crítico):', wsError);
        // No propagar el error, el examen puede funcionar sin WebSocket
      }
      console.log('✅ [EXAMEN-MULTI] Inicialización completada exitosamente');

    } catch (error: any) {
      console.error('❌ [EXAMEN-MULTI] Error al inicializar examen:', error);
      console.error('   - Mensaje:', error.message);
      console.error('   - Stack:', error.stack);
      console.error('   - Response:', error.response?.data);

      // NO mostrar notificaciones durante la primera carga
      // Solo loguear errores en consola durante la carga inicial
      if (!primeraLlamada && !examenData) {
        // Solo mostrar notificación si NO es la primera carga y hay un error crítico
        const esErrorDeRed = error.message?.includes('Network') ||
                            error.message?.includes('timeout') ||
                            error.code === 'ECONNABORTED' ||
                            error.response?.status === 503 ||
                            error.response?.status === 504;

        if (!esErrorDeRed) {
          notifications.show({
            title: 'Error al cargar examen',
            message: error.response?.data?.message || error.message || 'No se pudo cargar el examen',
            color: 'red',
          });
          console.log('🔄 [EXAMEN-MULTI] Redirigiendo a sala por error...');
          navigate(`/multijugador/sala/${sala!.codigoSala}`);
        } else {
          console.warn('⚠️ [EXAMEN-MULTI] Error de red temporal - no se muestra notificación');
        }
      } else {
        console.warn('⚠️ [EXAMEN-MULTI] Primera carga - errores no se muestran al usuario');
      }
    } finally {
      console.log('🏁 [EXAMEN-MULTI] Finalizando inicialización - setLoading(false)');
      setPrimeraLlamada(false); // Marcar que ya no es la primera llamada
      setLoading(false);
    }
  };

  const conectarWebSocket = async () => {
    try {
      await webSocketService.connect();
      webSocketService.suscribirseASala(sala!.codigoSala, {
        onActualizacionRanking: (ranking: RankingSalaDTO) => {
          // Mostrar notificación cuando alguien termine
          const terminados = ranking.ranking.filter(p => p.finalizoExamen);
          if (terminados.length > 0) {
            notifications.show({
              title: 'Ranking actualizado',
              message: `${terminados.length}/${ranking.totalParticipantes} han terminado`,
              color: 'blue',
            });
          }
        },
        onSalaFinalizada: () => {
          notifications.show({
            title: '¡Sala finalizada!',
            message: 'Todos los participantes han terminado',
            color: 'green',
          });
        }
      });
    } catch (error) {
      console.warn('⚠️ [WEBSOCKET] No se pudo conectar WebSocket (continuando sin actualizaciones en tiempo real):', error);
      // No lanzar error, el examen puede funcionar sin WebSocket
    }
  };

  const iniciarTemporizador = (inicio: Date) => {
    console.log('⏱️ [EXAMEN-MULTI] Iniciando temporizador con fecha:', inicio);
    intervalRef.current = setInterval(() => {
      setTiempoTranscurrido(prev => {
        const ahora = new Date();
        const tiempoEnSegundos = Math.floor((ahora.getTime() - inicio.getTime()) / 1000);
        return tiempoEnSegundos;
      });
    }, 1000);
  };

  const manejarRespuesta = (orden: number, respuesta: string | null) => {
    setRespuestas(prev => new Map(prev.set(orden, respuesta)));
  };

  const navegarPregunta = (direccion: 'anterior' | 'siguiente' | number) => {
    if (!examenData) return;

    let nuevaPregunta: number;
    if (typeof direccion === 'number') {
      nuevaPregunta = direccion;
    } else {
      nuevaPregunta = direccion === 'siguiente' 
        ? Math.min(preguntaActual + 1, examenData.totalPreguntas - 1)
        : Math.max(preguntaActual - 1, 0);
    }

    setPreguntaActual(nuevaPregunta);
  };

  const finalizarExamen = async () => {
    if (!examenData || !intentoId) return;

    try {
      setFinalizando(true);

      // Preparar respuestas
      const respuestasUsuario: RespuestaUsuario[] = examenData.preguntas.map(pregunta => ({
        orden: pregunta.orden,
        respuesta: respuestas.get(pregunta.orden) || null,
        tiempoSeg: Math.floor(tiempoTranscurrido / examenData.preguntas.length) // Tiempo promedio por pregunta
      }));

      const finalizarRequest: FinalizarExamenRequest = {
        intentoId,
        duracionSeg: tiempoTranscurrido,
        respuestas: respuestasUsuario
      };

      // Finalizar usando el servicio multijugador
      const ranking = await MultijugadorService.finalizarParticipacion(
        sala!.codigoSala,
        finalizarRequest
      );

      console.log('🎯 [EXAMEN] Examen finalizado - obteniendo estado actualizado de sala');
      console.log('   - Ranking:', ranking);
      console.log('   - Participantes terminados:', ranking.participantesTerminados);
      console.log('   - Total participantes:', ranking.totalParticipantes);

      // Recargar la sala para obtener el estado actualizado (FINALIZADA o EN_PROGRESO)
      const salaActualizada = await MultijugadorService.obtenerSala(sala!.codigoSala);
      console.log('✅ [EXAMEN] Sala recargada - Estado:', salaActualizada.estado);

      // Navegar a la página correspondiente según el estado
      console.log('🎯 [EXAMEN] Navegando a sala con estado actualizado');
      navigate(`/multijugador/sala/${sala!.codigoSala}`, {
        state: {
          sala: salaActualizada,
          esperandoResultados: true,
          participantesTerminados: ranking.participantesTerminados || 1
        }
      });

      console.log('✅ [EXAMEN] Navegación completada');

    } catch (error: any) {
      console.error('Error al finalizar examen:', error);
      notifications.show({
        title: 'Error al finalizar examen',
        message: error.message,
        color: 'red',
      });
    } finally {
      setFinalizando(false);
    }
  };

  const togglePausa = () => {
    setPausado(prev => !prev);
    if (pausado && tiempoInicio) {
      // Reanudar: Ajustar tiempo de inicio
      const tiempoAjustado = new Date(Date.now() - tiempoTranscurrido * 1000);
      setTiempoInicio(tiempoAjustado);
    }
  };

  const formatearTiempo = (segundos: number) => {
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;
    
    if (horas > 0) {
      return `${horas}:${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
    }
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  const calcularProgreso = () => {
    if (!examenData) return 0;
    return (respuestas.size / examenData.totalPreguntas) * 100;
  };

  if (!sala || !currentUser) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Text color="red" size="lg">Acceso no autorizado</Text>
          <Text size="sm" color="dimmed">Redirigiendo...</Text>
        </Stack>
      </Center>
    );
  }

  // Mostrar loader durante la carga o si no hay datos del examen
  if (loading || !examenData) {
    return (
      <Center h="100vh">
        <Stack align="center">
          <Loader size="lg" />
          <Text>Cargando examen multijugador...</Text>
          <Text size="sm" color="dimmed">Sala: {sala.nombreSala}</Text>
        </Stack>
      </Center>
    );
  }

  const pregunta = examenData.preguntas[preguntaActual];
  const respuestaActual = respuestas.get(pregunta.orden);
  const tiempoLimite = (sala.tiempoLimiteMin || 60) * 60;
  const tiempoRestante = Math.max(0, tiempoLimite - tiempoTranscurrido);

  return (
    <Box className="min-h-screen bg-gray-50">
      {/* Header multijugador */}
      <Paper shadow="sm" p="md" withBorder className="mb-6">
        <Group justify="space-between">
          <Group gap="md">
            <Badge leftSection={<IconUsers size={14} />} variant="light" color="blue">
              {sala.nombreSala}
            </Badge>
            <Badge leftSection={<IconTrophy size={14} />} variant="light" color="green">
              Multijugador
            </Badge>
          </Group>
          
          <Group gap="md">
            <Badge 
              leftSection={<IconClock size={14} />} 
              variant="light" 
              color={tiempoRestante < 300 ? 'red' : 'orange'}
              size="lg"
            >
              {formatearTiempo(tiempoRestante)}
            </Badge>
            
            <ActionIcon
              size="lg"
              variant="light"
              color={pausado ? 'green' : 'orange'}
              onClick={togglePausa}
            >
              {pausado ? <IconPlayerPlay size={18} /> : <IconPlayerPause size={18} />}
            </ActionIcon>
          </Group>
        </Group>
      </Paper>

      {/* Progreso */}
      <Card withBorder p="md" className="mb-6">
        <Group justify="space-between" mb="xs">
          <Text size="sm" fw={500}>Progreso del examen</Text>
          <Text size="sm" color="dimmed">
            {respuestas.size}/{examenData.totalPreguntas} respondidas
          </Text>
        </Group>
        <Progress value={calcularProgreso()} color="blue" size="md" />
      </Card>

      {/* Alerta de pausa */}
      {pausado && (
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Examen pausado"
          color="orange"
          className="mb-6"
        >
          El tiempo sigue corriendo para otros participantes. ¡Continúa pronto!
        </Alert>
      )}

      {/* Contenido de la pregunta */}
      <div className="max-w-4xl mx-auto px-4">
        <Paper shadow="sm" p="xl" withBorder className="mb-6">
          <Stack gap="lg">
            {/* Número de pregunta */}
            <Group justify="space-between">
              <Badge size="lg" variant="light" color="blue">
                Pregunta {pregunta.orden} de {examenData.totalPreguntas}
              </Badge>
              <Group gap="xs">
                <Text size="sm" color="dimmed">Tiempo transcurrido:</Text>
                <Text size="sm" fw={500}>{formatearTiempo(tiempoTranscurrido)}</Text>
              </Group>
            </Group>

            {/* Caso clínico si existe */}
            {pregunta.tipo === 'CASO' && pregunta.casoEstudioContenido && (
              <Paper p="md" withBorder className="bg-blue-50">
                <Title order={4} mb="sm" className="text-blue-700">
                  Caso Clínico
                </Title>
                <Text size="sm" className="whitespace-pre-wrap">
                  {pregunta.casoEstudioContenido}
                </Text>
                {pregunta.imagenCaso && (
                  <img 
                    src={pregunta.imagenCaso} 
                    alt="Imagen del caso" 
                    className="max-w-full h-auto mt-4 rounded-lg"
                  />
                )}
              </Paper>
            )}

            {/* Pregunta */}
            <div>
              <Text size="lg" fw={500} mb="md">
                {pregunta.pregunta}
              </Text>
              {/* Especialidad y Tema Badges */}
              <div style={{ marginBottom: '16px' }}>
                {pregunta.especialidadNombre && (
                  <Badge
                    size="md"
                    variant="light"
                    color="blue"
                    leftSection={<IconBook size={13} />}
                    style={{ marginRight: '8px', display: 'inline-block' }}
                  >
                    {pregunta.especialidadNombre}
                  </Badge>
                )}
                {pregunta.temaNombre && (
                  <Badge
                    size="md"
                    variant="light"
                    color="cyan"
                    leftSection={<IconTag size={13} />}
                    style={{ display: 'inline-block' }}
                  >
                    {pregunta.temaNombre}
                  </Badge>
                )}
              </div>
              {pregunta.imagenPregunta && (
                <img
                  src={pregunta.imagenPregunta}
                  alt="Imagen de la pregunta"
                  className="max-w-full h-auto mb-4 rounded-lg"
                />
              )}
            </div>

            {/* Opciones */}
            <Radio.Group
              value={respuestaActual || ''}
              onChange={(value) => manejarRespuesta(pregunta.orden, value)}
              size="md"
            >
              <Stack gap="md">
                <Radio value="A" label={`A) ${pregunta.respuestaA}`} />
                <Radio value="B" label={`B) ${pregunta.respuestaB}`} />
                <Radio value="C" label={`C) ${pregunta.respuestaC}`} />
                <Radio value="D" label={`D) ${pregunta.respuestaD}`} />
              </Stack>
            </Radio.Group>
          </Stack>
        </Paper>

        {/* Navegación */}
        <Paper shadow="sm" p="md" withBorder className="mb-6">
          <Group justify="space-between">
            <Button
              variant="outline"
              onClick={() => navegarPregunta('anterior')}
              disabled={preguntaActual === 0}
            >
              Anterior
            </Button>

            <Group gap="xs">
              {examenData.preguntas.map((_, index) => (
                <ActionIcon
                  key={index}
                  variant={respuestas.has(index + 1) ? 'filled' : 'outline'}
                  color={index === preguntaActual ? 'blue' : 'gray'}
                  onClick={() => navegarPregunta(index)}
                  size="sm"
                >
                  {index + 1}
                </ActionIcon>
              ))}
            </Group>

            {preguntaActual === examenData.totalPreguntas - 1 ? (
              <Button
                leftSection={<IconCheck size={18} />}
                color="green"
                onClick={finalizarExamen}
                loading={finalizando}
                disabled={finalizando}
              >
                Finalizar Examen
              </Button>
            ) : (
              <Button
                onClick={() => navegarPregunta('siguiente')}
                disabled={preguntaActual === examenData.totalPreguntas - 1}
              >
                Siguiente
              </Button>
            )}
          </Group>
        </Paper>

        {/* Botón de queja/sugerencia */}
        <div className="flex justify-center">
          <QuejaSugerenciaButton
            tipoObjetivo={pregunta.tipo === 'CASO' ? 'PREGUNTA_CASO' : 'REACTIVO'}
            reactivoId={pregunta.tipo === 'REACTIVO' ? pregunta.orden : undefined}
            preguntaCasoId={pregunta.tipo === 'CASO' ? pregunta.orden : undefined}
          />
        </div>
      </div>
    </Box>
  );
};

export default ExamenMultijugadorPage;